// frontend/src/pages/Login.jsx  — FULL REPLACEMENT
// Supports: Google OAuth, email/password login, sign up, forgot password
// No more redirect to external auth.emergentagent.com

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";

const MODE = { LOGIN: "login", SIGNUP: "signup", FORGOT: "forgot" };

export default function Login() {
  const { user, loading, signInWithGoogle, signInWithEmail, signUpWithEmail, sendPasswordReset } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState(MODE.LOGIN);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogle = async () => {
    try {
      await signInWithGoogle();
      // Supabase handles redirect — no navigate needed
    } catch (e) {
      toast.error("Accesso Google fallito. Riprova.");
    }
  };

  const handleEmailAction = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === MODE.FORGOT) {
        await sendPasswordReset(email);
        setForgotSent(true);
      } else if (mode === MODE.SIGNUP) {
        await signUpWithEmail(email, password, name);
        toast.success("Account creato! Controlla la tua email per confermare.");
        setMode(MODE.LOGIN);
      } else {
        await signInWithEmail(email, password);
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      const msg = e?.message || "Errore sconosciuto";
      if (msg.includes("Invalid login credentials")) {
        toast.error("Email o password errati.");
      } else if (msg.includes("Email not confirmed")) {
        toast.error("Conferma prima la tua email.");
      } else if (msg.includes("User already registered")) {
        toast.error("Email già registrata. Accedi o resetta la password.");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    [MODE.LOGIN]: "Bentornato.",
    [MODE.SIGNUP]: "Crea il tuo account.",
    [MODE.FORGOT]: "Reimposta la password.",
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-white flex items-center justify-center px-5"
    >
      <div className="w-full max-w-md border border-neutral-200 rounded-[14px] p-8 md:p-12 bg-white">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 select-none">
          <span
            aria-hidden
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: "#c9a86a" }}
          />
          <span className="font-display tracking-[0.28em] text-[13px] md:text-sm font-medium text-neutral-950">
            WEDFLOW
          </span>
        </a>

        <h1 className="mt-10 font-display text-[34px] md:text-[44px] tracking-[-0.02em] font-light text-neutral-950 leading-[1.05]">
          {titles[mode]}
        </h1>

        {/* Google Button — shown on login + signup */}
        {mode !== MODE.FORGOT && (
          <>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              data-testid="login-google-btn"
              className="mt-8 w-full flex items-center justify-center gap-3 rounded-full py-3.5 border border-neutral-300 bg-white hover:bg-neutral-50 transition-all duration-300 text-[14px] font-medium disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                <path fill="#4285F4" d="M17.64 9.2a10.4 10.4 0 0 0-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.79 2.71v2.26h2.9c1.7-1.57 2.69-3.88 2.69-6.62z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.36 0-4.36-1.6-5.07-3.74H.94v2.34A9 9 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.93 10.68A5.4 5.4 0 0 1 3.65 9c0-.58.1-1.15.28-1.68V4.98H.94A9 9 0 0 0 0 9c0 1.45.35 2.82.94 4.02l2.99-2.34z" />
                <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.34l2.58-2.58A8.96 8.96 0 0 0 9 0 9 9 0 0 0 .94 4.98l2.99 2.34C4.64 5.18 6.64 3.58 9 3.58z" />
              </svg>
              Continua con Google
            </button>

            <div className="my-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-neutral-200" />
              <span className="text-[12px] text-neutral-400">oppure</span>
              <div className="flex-1 h-px bg-neutral-200" />
            </div>
          </>
        )}

        {/* Email form */}
        <form onSubmit={handleEmailAction} className="space-y-4">
          {mode === MODE.SIGNUP && (
            <div>
              <label className="block text-[13px] text-neutral-600 mb-1.5">Nome completo</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Mario Rossi"
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[#c9a86a]/40 focus:border-[#c9a86a] transition"
              />
            </div>
          )}

          <div>
            <label className="block text-[13px] text-neutral-600 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="tu@esempio.it"
              className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[#c9a86a]/40 focus:border-[#c9a86a] transition"
            />
          </div>

          {mode !== MODE.FORGOT && (
            <div>
              <label className="block text-[13px] text-neutral-600 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder="Minimo 8 caratteri"
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[#c9a86a]/40 focus:border-[#c9a86a] transition"
              />
            </div>
          )}

          {forgotSent ? (
            <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-[13px] text-green-700">
              Email inviata! Controlla la tua casella di posta.
            </div>
          ) : (
            <button
              type="submit"
              disabled={busy}
              className="w-full py-3.5 rounded-full text-[14px] font-medium text-white disabled:opacity-50 transition-all"
              style={{ background: "#c9a86a" }}
            >
              {busy ? "..." : mode === MODE.LOGIN ? "Accedi" : mode === MODE.SIGNUP ? "Crea account" : "Invia link di reset"}
            </button>
          )}
        </form>

        {/* Mode switchers */}
        <div className="mt-6 flex flex-col gap-2 text-center">
          {mode === MODE.LOGIN && (
            <>
              <button
                type="button"
                onClick={() => setMode(MODE.FORGOT)}
                className="text-[13px] text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                Password dimenticata?
              </button>
              <button
                type="button"
                onClick={() => setMode(MODE.SIGNUP)}
                className="text-[13px] text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                Non hai un account? <span className="font-medium text-neutral-900">Registrati</span>
              </button>
            </>
          )}
          {mode === MODE.SIGNUP && (
            <button
              type="button"
              onClick={() => setMode(MODE.LOGIN)}
              className="text-[13px] text-neutral-500 hover:text-neutral-900 transition-colors"
            >
              Hai già un account? <span className="font-medium text-neutral-900">Accedi</span>
            </button>
          )}
          {mode === MODE.FORGOT && (
            <button
              type="button"
              onClick={() => { setMode(MODE.LOGIN); setForgotSent(false); }}
              className="text-[13px] text-neutral-500 hover:text-neutral-900 transition-colors"
            >
              ← Torna al login
            </button>
          )}
        </div>

        <p className="mt-10 text-[11px] tracking-[0.18em] uppercase text-neutral-400 text-center">
          Privacy garantita · Dati sicuri
        </p>
      </div>
    </div>
  );
}
